import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Outlet, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { resolveCountryName, resolveCountryIso3 } from '@upbeat/shared';
import { useMeta } from './api/hooks';
import { ErrorState } from './components/EmptyState';
import { Filters, hasActiveFilters, ROUTE_FILTER_CONFIG, DEFAULT_FILTER_CONFIG } from './components/Filters';
import { Nav } from './components/Nav';
import { Sheet, SheetContent } from './components/ui/sheet';
import { Benchmarking } from './pages/Benchmarking';
import { MapPage } from './pages/MapPage';
import { Overview } from './pages/Overview';
import type { PageContext } from './pages/PageShell';
import { Trends } from './pages/Trends';
import { choicesFromMeta } from './state/filters';
import { useFilters } from './state/useFilters';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

function Layout() {
  const meta = useMeta();
  const choices = useMemo(() => (meta.data ? choicesFromMeta(meta.data) : null), [meta.data]);
  const { state, query, update, reset } = useFilters(choices);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Synchronize comparison countries with URL (?countries=CIV,TUN). Unselected (empty) by default.
  // The URL stores standardized 3-letter ISO codes (e.g. CIV, TUN) to prevent character encoding bugs,
  // while selectedCountries in state holds canonical country names for UI components.
  const selectedCountries = useMemo(() => {
    const raw = searchParams.get('countries');
    if (!raw || !raw.trim()) return [];
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => resolveCountryName(s))
      .filter(Boolean)
      .slice(0, 4);
  }, [searchParams]);

  const setSelectedCountries = useCallback(
    (nextOrUpdater: string[] | ((prev: string[]) => string[])) => {
      setSearchParams(
        (prev) => {
          const raw = prev.get('countries');
          const current = raw
            ? raw
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
                .map((s) => resolveCountryName(s))
                .filter(Boolean)
            : [];
          const next =
            typeof nextOrUpdater === 'function' ? nextOrUpdater(current) : nextOrUpdater;
          const clamped = next.slice(0, 4);
          const copy = new URLSearchParams(prev);
          if (clamped.length === 0) {
            copy.delete('countries');
          } else {
            // Write clean ISO-3 codes into the URL
            const isoCodes = clamped.map((c) => resolveCountryIso3(c) ?? c);
            copy.set('countries', isoCodes.join(','));
          }
          return copy;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const country = selectedCountries[0] ?? null;
  const setCountry = (c: string | null) => {
    if (c === null) setSelectedCountries([]);
    else setSelectedCountries((prev) => (prev.includes(c) ? prev : [...prev.slice(-3), c]));
  };
  const location = useLocation();
  useEffect(() => setMenuOpen(false), [location.pathname]);

  const handleReset = () => {
    reset();
    setSelectedCountries([]);
  };

  const config = ROUTE_FILTER_CONFIG[location.pathname] ?? DEFAULT_FILTER_CONFIG;
  const activeCountry = selectedCountries.length > 0 ? selectedCountries.join(',') : null;
  const hasFilters = hasActiveFilters(state, meta.data, config, activeCountry);

  const ctx: PageContext = {
    meta: meta.data,
    state,
    query,
    country,
    setCountry,
    selectedCountries,
    setSelectedCountries,
  };
  return (
    <>
      <Nav
        menuOpen={menuOpen}
        onToggleMenu={() => setMenuOpen((o) => !o)}
        hasActiveFilters={hasFilters}
      />
      <div className="shell">
        {/* Desktop Sidebar */}
        <div className="sidebar-desktop hidden lg:block">
          <Filters
            meta={meta.data}
            state={state}
            open={false}
            onChange={update}
            onReset={handleReset}
            country={country}
            onCountryChange={setCountry}
            selectedCountries={selectedCountries}
            onSelectedCountriesChange={setSelectedCountries}
          />
        </div>

        {/* Mobile Sheet Drawer */}
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetContent side="left" className="w-[320px] sm:w-[360px] p-0 border-r border-hair bg-space backdrop-blur-none z-50">
            <div className="h-full overflow-y-auto px-6 py-6 pt-12">
              <Filters
                meta={meta.data}
                state={state}
                open={true}
                onChange={(patch) => update(patch)}
                onReset={handleReset}
                country={country}
                onCountryChange={setCountry}
                selectedCountries={selectedCountries}
                onSelectedCountriesChange={setSelectedCountries}
              />
            </div>
          </SheetContent>
        </Sheet>

        <main className="main">
          {meta.isError ? (
            <section className="section"><ErrorState error={meta.error} /></section>
          ) : (
            <Outlet context={ctx} />
          )}
        </main>
      </div>
    </>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, '')}>
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<Overview />} />
            <Route path="trends" element={<Trends />} />
            <Route path="benchmarking" element={<Benchmarking />} />
            <Route path="map" element={<MapPage />} />
            <Route path="*" element={<Overview />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
